'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Shared open/close behaviour for the sidebar's mobile ☰ nav drawer, used by
 * both the end-user AppShell and the internal AdminShell so the two chromes
 * stay identical: route-change auto-close, outside-tap close, and Escape
 * (which returns focus to the toggle). The open/close *animation* lives in
 * ui-kit `kiao-app.css` (`.side-nav` / `.is-open`).
 */
export function useSidebarNav() {
  const pathname = usePathname()
  const [navOpen, setNavOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const navToggleRef = useRef<HTMLButtonElement>(null)
  const closeNav = useCallback(() => setNavOpen(false), [])
  const toggleNav = useCallback(() => setNavOpen((open) => !open), [])

  // The shell is mounted once by the route-group layout, so it survives
  // navigation — close the drawer when the route changes.
  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  // Close the drawer on outside-tap + Escape (returns focus to the toggle).
  useEffect(() => {
    if (!navOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!sidebarRef.current?.contains(event.target as Node)) closeNav()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeNav()
        navToggleRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [navOpen, closeNav])

  return { navOpen, toggleNav, closeNav, sidebarRef, navToggleRef }
}
